import type { Product_Output, StaffMember } from '@/api'
import { StaffCapability } from '@/api'
import type { ShiftType } from '../hooks/useDetailedPlanFlow'

// スタッフ名ではなく枠を表す名前。重複割当の上限チェック対象外。
export const STOCKER_NAME = 'ストッカー'

export const isStocker = (staffName: string | null | undefined): boolean => staffName === STOCKER_NAME

export const STAFF_COLOR_CLASSES = [
  'bg-blue-100 text-blue-900',
  'bg-blue-200 text-blue-900',
  'bg-green-100 text-green-900',
  'bg-green-200 text-green-900',
  'bg-orange-100 text-orange-900',
  'bg-orange-200 text-orange-900',
  'bg-purple-100 text-purple-900',
  'bg-purple-200 text-purple-900',
  'bg-pink-100 text-pink-900',
  'bg-pink-200 text-pink-900',
  'bg-cyan-100 text-cyan-900',
  'bg-cyan-200 text-cyan-900',
  'bg-indigo-100 text-indigo-900',
  'bg-indigo-200 text-indigo-900',
  'bg-teal-100 text-teal-900',
  'bg-teal-200 text-teal-900',
  'bg-yellow-100 text-yellow-900',
  'bg-yellow-200 text-yellow-900',
  'bg-red-100 text-red-900',
  'bg-red-200 text-red-900',
  'bg-bluegray-100 text-bluegray-900',
  'bg-bluegray-200 text-bluegray-900',
  'bg-primary-100 text-primary-900',
  'bg-primary-200 text-primary-900',
]

/** スタッフ名 → 表示色クラス。staffList の登場順で色を割り当てる。 */
export const buildStaffColorMap = (staffList: StaffMember[]): Record<string, string> => {
  const uniqueNames = Array.from(new Set(staffList.map((staff) => staff.name)))
  return uniqueNames.reduce<Record<string, string>>((acc, name, index) => {
    acc[name] = STAFF_COLOR_CLASSES[index % STAFF_COLOR_CLASSES.length]
    return acc
  }, {})
}

export interface StaffOption {
  label: string
  value: string | null
}

// オペレーター欄の擬似機械ID。各シフトの割当内にこのキーで保持される。
export const OPERATOR_MACHINE_ID = '__operator__'

/** 「同日に2件まで」の上限に当たったスタッフの強調表示時間 */
export const LIMIT_HIGHLIGHT_MS = 5000

/** 上限に当たったスタッフの既存割当枠を強調する outline（レイアウトを崩さないよう outline を使う） */
export const LIMIT_HIGHLIGHT_STYLE: React.CSSProperties = { outline: '2px solid var(--red-500)', outlineOffset: '1px' }

export interface StaffLocation {
  shift: ShiftType
  machineId: string
}

/** 指定日の割当から、スタッフが入っている枠（シフト × 成形機）を列挙する。 */
export const findStaffLocations = (
  dayAssignments: Partial<Record<ShiftType, Record<string, (string | null)[]>>> | undefined,
  staffName: string,
): StaffLocation[] => {
  const out: StaffLocation[] = []
  for (const shift of ['day', 'evening', 'night'] as ShiftType[]) {
    for (const [machineId, slots] of Object.entries(dayAssignments?.[shift] ?? {})) {
      if ((slots ?? []).includes(staffName)) out.push({ shift, machineId })
    }
  }
  return out
}

/** 「夕 100EVA、夜 J850AD-1」形式の表示文字列にする。 */
export const formatStaffLocations = (locations: StaffLocation[], shiftLabels: Record<ShiftType, string>): string =>
  locations
    .map((l) => `${shiftLabels[l.shift]} ${l.machineId === OPERATOR_MACHINE_ID ? 'オペレーター' : l.machineId}`)
    .join('、')

/** シフトごとの Dropdown 選択肢（先頭は未割当）。 */
export const buildStaffOptionsByShift = (staffList: StaffMember[]): Record<ShiftType, StaffOption[]> => {
  const shifts: ShiftType[] = ['day', 'evening', 'night']
  return Object.fromEntries(
    shifts.map((shift) => [
      shift,
      [
        { label: '（未割当）', value: null },
        ...staffList.filter((s) => s.shift === shift).map((s) => ({ label: s.name, value: s.name })),
      ],
    ]),
  ) as Record<ShiftType, StaffOption[]>
}

/** 便蓋品かどうか判定 */
export const isLidded = (product: Product_Output | undefined): boolean => {
  if (product?.is_lidded_or_appearance !== undefined && product.is_lidded_or_appearance !== null) {
    return product.is_lidded_or_appearance === 'lidded'
  }
  if (!product?.category) return false
  return product.category.includes('便蓋')
}

/** 外観品かどうか判定 */
export const isAppearance = (product: Product_Output | undefined): boolean => {
  if (product?.is_lidded_or_appearance !== undefined && product.is_lidded_or_appearance !== null) {
    return product.is_lidded_or_appearance === 'appearance'
  }
  if (!product?.category) return false
  return product.category.includes('底板') || product.category.includes('カバー')
}

/** 製品カテゴリラベルを返す */
export const getCategoryLabel = (product: Product_Output | undefined): { label: string; className: string } => {
  if (isLidded(product)) return { label: '便蓋', className: 'text-green-500' }
  if (isAppearance(product)) return { label: '外観品', className: 'text-green-500' }
  return { label: 'その他', className: '' }
}

/**
 * 生産品（SKU一覧）に対して、割当スタッフ全員を横断して担当可否をチェックし警告文を返す。
 * 少なくとも1人が担当可能(DISABLED以外)であれば警告なし。全員が不可の場合のみ警告を出す。
 */
export const getStaffCapabilityWarnings = (
  staffNames: (string | null)[],
  skuIds: string[],
  staffByName: Record<string, StaffMember>,
  productBySku: Record<string, Product_Output>,
): string[] => {
  const assignedStaff = staffNames
    .filter((n): n is string => !!n)
    .map((n) => staffByName[n])
    .filter((s): s is StaffMember => !!s)
  if (assignedStaff.length === 0 || skuIds.length === 0) return []

  const warnings: string[] = []
  const seen = new Set<string>()
  for (const skuId of skuIds) {
    if (seen.has(skuId)) continue
    seen.add(skuId)
    const product = productBySku[skuId]
    if (!product) continue
    let catName: string
    let getCapability: (s: StaffMember) => StaffCapability | undefined
    if (isLidded(product)) {
      catName = '便蓋'
      getCapability = (s) => s.can_handle_lidded
    } else if (isAppearance(product)) {
      catName = '外観品'
      getCapability = (s) => s.can_handle_appearance
    } else {
      catName = 'その他'
      getCapability = (s) => s.can_handle_other
    }
    const capabilities = assignedStaff.map(getCapability)
    // 1人でも担当可能(DISABLED以外かつ定義済み)なら警告不要
    const anyCapable = capabilities.some((c) => c !== undefined && c !== StaffCapability.DISABLED)
    if (anyCapable) continue
    // 全員が不可 or 一部可の場合のみ警告
    const anyPartial = capabilities.some((c) => c === StaffCapability.PARTIAL)
    warnings.push(anyPartial ? `${skuId}(${catName})一部可` : `${skuId}(${catName})担当不可`)
  }
  return warnings
}
